import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  UnderCarriageForm,
  UnderCarriageDetailResponse,
  UnderCarriageListResponse,
  UnderCarriageUpdate,
} from "../types";
import {
  selectUnderCarriageForm,
  selectUnderCarriagePage,
  selectUnderCarriagePerPage,
} from "./selector";
import { underCarriageActions } from "./slice";

let isFetchingList = false;

export function* handleGetUnderCarriageList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectUnderCarriagePage);
    const perPage: number = yield select(selectUnderCarriagePerPage);

    const response: UnderCarriageListResponse = yield call(
      request,
      `${API_URL}/sub-chassis/list?page=${page}&perPage=${perPage}`,
      { method: "GET" }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(underCarriageActions.getUnderCarriageListSuccess(payload));
    } else {
      yield put(underCarriageActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectUnderCarriagePage);

    if (page === 1) {
      yield put(underCarriageActions.getUnderCarriageListError(message));
    } else {
      yield put(underCarriageActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddUnderCarriage() {
  const form: UnderCarriageForm = yield select(selectUnderCarriageForm);

  try {
    const response = yield call(request, `${API_URL}/sub-chassis`, {
      method: "POST",
      body: JSON.stringify({
        subChassisId: form.code.trim(),
        subChassisName: form.description.trim(),
        subChassisHeight: Number(form.height),
      }),
    });

    yield put(underCarriageActions.addUnderCarriageSuccess(response.data));
  } catch (error) {
    yield put(
      underCarriageActions.addUnderCarriageError((error as Error).message)
    );
  }
}

export function* handleUpdateUnderCarriage(
  action: PayloadAction<UnderCarriageUpdate>
) {
  const { id, subChassisName, subChassisHeight } = action.payload;

  try {
    yield call(request, `${API_URL}/sub-chassis/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        subChassisName: subChassisName.trim(),
        subChassisHeight,
      }),
    });

    yield put(underCarriageActions.updateUnderCarriageSuccess());
    yield put(underCarriageActions.getUnderCarriageList());
  } catch (error) {
    yield put(
      underCarriageActions.updateUnderCarriageError((error as Error).message)
    );
  }
}

export function* handleDeleteUnderCarriage(action: PayloadAction<string>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/sub-chassis/${id}`, {
      method: "DELETE",
    });

    yield put(underCarriageActions.deleteUnderCarriageSuccess(id));
  } catch (error) {
    yield put(
      underCarriageActions.deleteUnderCarriageError((error as Error).message)
    );
  }
}

export function* handleGetUnderCarriageDetail(action: PayloadAction<string>) {
  const id = action.payload;

  try {
    const response: UnderCarriageDetailResponse = yield call(
      request,
      `${API_URL}/sub-chassis/${id}`,
      { method: "GET" }
    );

    yield put(
      underCarriageActions.getUnderCarriageDetailSuccess(response?.data)
    );
  } catch (error) {
    yield put(
      underCarriageActions.getUnderCarriageDetailError(
        (error as Error).message
      )
    );
  }
}

export function* underCarriageSaga() {
  yield takeLeading(
    [
      underCarriageActions.getUnderCarriageList.type,
      underCarriageActions.getNextPageItems.type,
    ],
    handleGetUnderCarriageList
  );
  yield takeLatest(
    underCarriageActions.addUnderCarriage.type,
    handleAddUnderCarriage
  );
  yield takeLatest(
    underCarriageActions.updateUnderCarriage.type,
    handleUpdateUnderCarriage
  );
  yield takeLatest(
    underCarriageActions.deleteUnderCarriage.type,
    handleDeleteUnderCarriage
  );
  yield takeLatest(
    underCarriageActions.getUnderCarriageDetail.type,
    handleGetUnderCarriageDetail
  );
}

export default underCarriageSaga;
